/**
 * File Validation Utility
 *
 * Uses FILE EXTENSION validation to block invalid files.
 *
 * Usage:
 *   import { validateDocumentFile } from '../utils/fileValidation';
 *
 *   const result = validateDocumentFile(file);
 *   if (!result.valid) {
 *     notification.error({ message: 'Invalid File', description: result.error });
 *     return; // Don't add file to list
 *   }
 */

// Get file extension from filename
const getExtension = (fileName) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

// Format bytes to readable string
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Validate file for DOCUMENTS (PDF, Word, Excel, TXT, CSV, JSON)
 * Max size: 100MB
 */
export const validateDocumentFile = (file) => {
  const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'json'];
  const maxSizeMB = 100;

  if (!file || !file.name) {
    return { valid: false, error: 'No file selected' };
  }

  // Check extension FIRST
  const ext = getExtension(file.name);
  if (!ext || !allowedExtensions.includes(ext)) {
    return {
      valid: false,
      error: `Invalid file type ".${ext || 'unknown'}". Only PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, JSON files are allowed.`,
    };
  }

  // Check file size
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File too large (${formatFileSize(file.size)}). Maximum: ${maxSizeMB}MB`,
    };
  }

  return { valid: true, error: null };
};

/**
 * Validate file for MEDIA (Image, Video, PDF)
 * Max size: 50MB
 */
export const validateMediaFile = (file) => {
  const allowedExtensions = ['png', 'jpg', 'jpeg', 'mp4', 'pdf'];
  const maxSizeMB = 50;

  if (!file || !file.name) {
    return { valid: false, error: 'No file selected' };
  }

  const ext = getExtension(file.name);
  if (!ext || !allowedExtensions.includes(ext)) {
    return {
      valid: false,
      error: `Invalid file type ".${ext || 'unknown'}". Only PNG, JPG, MP4, and PDF files are allowed.`,
    };
  }

  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File too large (${formatFileSize(file.size)}). Maximum: ${maxSizeMB}MB`,
    };
  }

  return { valid: true, error: null };
};

/**
 * Get media type category from file extension
 */
export const getMediaType = (fileName) => {
  const ext = getExtension(fileName);
  if (['png', 'jpg', 'jpeg'].includes(ext)) return 'image';
  if (ext === 'mp4') return 'video';
  if (ext === 'pdf') return 'pdf';
  return null;
};

/**
 * Validate file for SURVEY DOCS (PDF, Word, Excel, TXT, CSV)
 * Max size: 10MB
 */
export const validateSurveyDocFile = (file) => {
  const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];
  const maxSizeMB = 10;

  if (!file || !file.name) {
    return { valid: false, error: 'No file selected' };
  }

  // Check extension FIRST
  const ext = getExtension(file.name);
  if (!ext || !allowedExtensions.includes(ext)) {
    return {
      valid: false,
      error: `Invalid file type ".${ext || 'unknown'}". Only PDF, DOC, DOCX, XLS, XLSX, TXT, CSV files are allowed.`,
    };
  }

  // Check file size
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File too large (${formatFileSize(file.size)}). Maximum: ${maxSizeMB}MB`,
    };
  }

  return { valid: true, error: null };
};
